import React from 'react';
import { useLocation } from 'react-router-dom';
import { asText, excerpt, formatFeedbackDate } from '../utils/feedbackReplies';

/**
 * "Reply to your feedback" (v3.90.0) — shown at the top of the user's HOME
 * page (the role's landing route) when an administrator has answered a
 * message they sent through Send Feedback and they have not acknowledged it.
 *
 * In the page flow, not floating: it can never cover an assessment card, a
 * sticky table column or a modal, and it is not shown on any other page —
 * a teacher who signs in and goes straight to assessing a child is not
 * interrupted. It stays until "Got it" (the sender's one permitted write,
 * see firestore.rules → app_feedback) — the card goes at once and the write
 * follows (hooks/useFeedbackReplies.js); the reply can always be read again
 * under Send Feedback → Your messages.
 *
 * `readOnly`: an administrator viewing as this user sees the card but cannot
 * acknowledge it for them.
 */
export default function FeedbackReplyNotice({
  replies, landingRoute, onAcknowledge, onViewAll, error = null, readOnly = false,
}) {
  const location = useLocation();
  if (!Array.isArray(replies) || replies.length === 0) return null;
  if (location?.pathname !== landingRoute) return null;

  return (
    <section className="feedback-reply-notice no-print" aria-label="Replies to your feedback">
      {replies.map((reply) => {
        const sent = formatFeedbackDate(reply.timestamp);
        const answered = formatFeedbackDate(reply.replyAt);
        return (
          <article key={reply.id} className="feedback-reply-card" role="status">
            <div className="feedback-reply-head">
              <span className="feedback-reply-icon" aria-hidden="true">💬</span>
              <strong>Reply to your feedback</strong>
              <span className="feedback-reply-meta">
                {asText(reply.replyByName, 'Literacy Assessments')}{answered ? ` · ${answered}` : ''}
              </span>
            </div>
            <p className="feedback-reply-yours">
              You wrote{sent ? ` on ${sent}` : ''}: “{excerpt(reply.message)}”
            </p>
            <p className="feedback-reply-text">{asText(reply.replyText)}</p>
            <div className="feedback-reply-actions">
              <button
                type="button"
                className="btn-modern btn-modern-primary"
                onClick={() => onAcknowledge(reply.id)}
                disabled={readOnly}
              >
                Got it
              </button>
              <button type="button" className="btn-modern btn-modern-secondary" onClick={onViewAll}>
                Your messages
              </button>
            </div>
            {readOnly && (
              <p className="feedback-reply-note">
                You are viewing as this user. “Got it” is off, so the reply stays unread for them.
              </p>
            )}
          </article>
        );
      })}
      {error && (
        <p className="feedback-reply-error" role="alert">
“Got it” could not be saved. Please try again — the reply stays here until it is.
        </p>
      )}
    </section>
  );
}
